/**
 * Composant GeneratedArticleCard
 * Affiche un article généré avec ses options d'édition et de publication
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, Link2, Save, Pencil, Loader2, Check, ExternalLink, Eye, Code } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import SimpleEditor from '../SimpleEditor';
import VersionSelector from './VersionSelector';
import { ImageSelector } from './ImageSelector';

const API = process.env.REACT_APP_BACKEND_URL;

export const GeneratedArticleCard = ({
  siteId,
  rawArticleData,
  sites,
  publishedArticles,
  publishing,
  uploadingCustomImage,
  onUpdateArticle,
  onCustomImageUpload,
  onPublish,
}) => {
  const navigate = useNavigate();
  
  // Protection contre siteId undefined
  if (!siteId) {
    console.error('[GeneratedArticleCard] siteId is undefined!', { rawArticleData });
    return null;
  }
  
  const isExtra = siteId.startsWith('extra_');

  // Gérer les versions v1/v2
  const hasV1 = !!rawArticleData?.v1;
  const hasV2 = !!rawArticleData?.v2;
  const selectedVersion = rawArticleData?.selectedVersion || (hasV2 && !hasV1 ? 2 : 1);

  // Sélectionner l'article à afficher
  let articleData;
  if (hasV1 && hasV2) {
    articleData = selectedVersion === 2 ? rawArticleData.v2 : rawArticleData.v1;
  } else if (hasV2) {
    articleData = rawArticleData.v2;
  } else if (hasV1) {
    articleData = rawArticleData.v1;
  } else {
    articleData = rawArticleData;
  }

  const handleVersionChange = (version) => {
    onUpdateArticle(siteId, { selectedVersion: version }, true);
  };

  const handleFieldChange = (field, value) => {
    onUpdateArticle(siteId, { [field]: value });
  };

  const handleSave = async () => {
    try {
      const articleId = articleData.id || articleData.article_id;
      if (articleId) {
        await axios.put(`${API}/api/articles/${articleId}`, articleData);
        toast.success('Modifications sauvegardées');
      }
    } catch (err) {
      toast.error('Erreur de sauvegarde');
    }
  };

  const getFaviconUrl = (siteUrl) => {
    try {
      const url = new URL(siteUrl);
      return `https://www.google.com/s2/favicons?domain=${url.hostname}&sz=64`;
    } catch {
      return null;
    }
  };

  return (
    <Card className={`backdrop-blur-xl ${isExtra ? 'bg-amber-950/20 border-amber-500/30' : 'bg-slate-950/40 border-slate-800/60'}`}>
      <CardHeader>
        <div className="flex items-center gap-3">
          {isExtra ? (
            <div className="w-8 h-8 rounded bg-amber-500/20 border border-amber-500/40 flex items-center justify-center">
              <FileText className="w-4 h-4 text-amber-400" />
            </div>
          ) : (
            <img 
              src={getFaviconUrl(sites.find(s => s.id === siteId)?.url || '')} 
              alt="" 
              className="w-8 h-8 rounded"
            />
          )}
          <CardTitle className={`text-xl font-orbitron uppercase ${isExtra ? 'text-amber-400' : 'text-cyan-400'}`}>
            {articleData.site_name || (isExtra ? articleData.extra_label : siteId)}
          </CardTitle>
          {isExtra && <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded font-mono">HORS SITE</span>}
          {!isExtra && articleData.cluster_links_injected > 0 && (
            <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded font-mono flex items-center gap-1">
              <Link2 className="w-3 h-3" />
              {articleData.cluster_links_injected} lien{articleData.cluster_links_injected > 1 ? 's' : ''}
            </span>
          )}
        </div>
        
        {/* Sélecteur de version (v1/v2) */}
        {hasV1 && hasV2 && (
          <VersionSelector
            siteId={siteId}
            selectedVersion={selectedVersion}
            onSelectVersion={handleVersionChange}
            v1Article={rawArticleData.v1}
            v2Article={rawArticleData.v2}
          />
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Sélection d'images */}
        <ImageSelector
          siteId={siteId}
          articleData={articleData}
          isExtra={isExtra}
          sites={sites}
          uploadingCustomImage={uploadingCustomImage}
          onUpdateArticle={onUpdateArticle}
          onCustomImageUpload={onCustomImageUpload}
        />

        {/* Choix du titre (si propositions disponibles ou deux titres différents) */}
        {((articleData.title_proposals && articleData.title_proposals.length > 0) || 
          ((articleData.title_user || articleData.title_llm) && articleData.title_user !== articleData.title_llm)) && (
          <TitleChoice
            siteId={siteId}
            articleData={articleData}
            onUpdateArticle={onUpdateArticle}
          />
        )}

        {/* SEO Slug - Editable */}
        <div className="mb-3 p-2.5 bg-slate-900/50 rounded-lg border border-slate-700/50">
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-[10px] text-slate-500 uppercase tracking-wider flex items-center gap-2">
              <span>Slug URL</span>
              {articleData.custom_slug && (
                <span className="text-[9px] bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded">PERSONNALISÉ</span>
              )}
            </label>
            {articleData.custom_slug && (
              <button
                type="button"
                onClick={() => handleFieldChange('custom_slug', null)}
                className="text-[9px] text-slate-500 hover:text-amber-400 transition-colors"
              >
                Réinitialiser
              </button>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-slate-500 text-xs">/</span>
            <Input
              data-testid={`input-slug-${siteId}`}
              value={articleData.custom_slug || articleData.seo_slug || articleData.desired_slug || ''}
              onChange={(e) => {
                const cleanSlug = e.target.value
                  .toLowerCase()
                  .replace(/\s+/g, '-')
                  .replace(/[^a-z0-9-]/g, '')
                  .replace(/-+/g, '-');
                handleFieldChange('custom_slug', cleanSlug);
              }}
              onBlur={(e) => {
                const cleanSlug = e.target.value
                  .toLowerCase()
                  .replace(/\s+/g, '-')
                  .replace(/[^a-z0-9-]/g, '')
                  .replace(/-+/g, '-')
                  .replace(/^-|-$/g, '');
                // Si le slug nettoyé est vide ou identique au slug auto, supprimer custom_slug
                const autoSlug = articleData.seo_slug || articleData.desired_slug || '';
                if (!cleanSlug || cleanSlug === autoSlug) {
                  handleFieldChange('custom_slug', null);
                }
              }}
              placeholder="slug-personnalise"
              className="flex-1 h-8 bg-slate-950/50 border-slate-700 text-emerald-400 font-mono text-xs"
            />
          </div>
        </div>

        {/* Titre - Editable */}
        <div>
          <p className="text-xs text-slate-500 uppercase mb-1">Titre</p>
          <Input
            value={articleData.title || ''}
            onChange={(e) => handleFieldChange('title', e.target.value)}
            className="text-xl font-bold bg-slate-950/50 border-slate-700 text-slate-100"
          />
        </div>

        {/* Résumé - Editable */}
        <div>
          <p className="text-xs text-slate-500 uppercase mb-1">Résumé</p>
          <Textarea
            value={articleData.summary || ''}
            onChange={(e) => handleFieldChange('summary', e.target.value)}
            className="bg-slate-950/50 border-slate-700 text-slate-300 text-sm min-h-[60px]"
          />
        </div>

        {/* Badge et toggle pour la réécriture OpenAI */}
        {articleData.rewritten && (
          <div className="flex items-center justify-between p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
            <div className="flex items-center gap-2">
              <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded font-mono">
                ✨ Réécrit par {articleData.rewrite_model || 'OpenAI'}
              </span>
              {articleData.rewrite_cost_eur && (
                <span className="text-xs text-slate-500">
                  ({articleData.rewrite_cost_eur.toFixed(3)}€)
                </span>
              )}
            </div>
            {articleData.content_before_rewrite && (
              <button
                type="button"
                onClick={() => handleFieldChange('_showOriginal', !articleData._showOriginal)}
                className={`text-xs px-3 py-1 rounded transition-colors ${
                  articleData._showOriginal 
                    ? 'bg-amber-500/20 text-amber-400 border border-amber-500/50' 
                    : 'bg-slate-700/50 text-slate-400 hover:bg-slate-700'
                }`}
                data-testid={`toggle-original-${siteId}`}
              >
                {articleData._showOriginal ? '📝 Voir version réécrite' : '📄 Voir version originale (Claude)'}
              </button>
            )}
          </div>
        )}

        {/* Contenu - Editable avec éditeur WYSIWYG */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <p className="text-xs text-slate-500 uppercase">Contenu</p>
              {articleData._showOriginal && (
                <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded">
                  VERSION ORIGINALE (Claude)
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={() => handleFieldChange('_editMode', articleData._editMode === 'html' ? 'visual' : 'html')}
              className="flex items-center gap-1 text-xs text-slate-500 hover:text-cyan-400 transition-colors"
              title={articleData._editMode === 'html' ? 'Mode visuel' : 'Mode HTML'}
            >
              {articleData._editMode === 'html' ? (
                <><Eye className="w-3 h-3" /> Visuel</>
              ) : (
                <><Code className="w-3 h-3" /> HTML</>
              )}
            </button>
          </div>
          {articleData._editMode === 'html' ? (
            <Textarea
              value={articleData._showOriginal ? (articleData.content_before_rewrite || '') : (articleData.content || '')}
              onChange={(e) => !articleData._showOriginal && handleFieldChange('content', e.target.value)}
              className={`bg-slate-950/50 border-slate-700 text-slate-300 text-sm min-h-[200px] font-mono ${articleData._showOriginal ? 'opacity-75' : ''}`}
              data-testid={`content-html-${siteId}`}
              readOnly={articleData._showOriginal}
            />
          ) : (
            <SimpleEditor
              value={articleData._showOriginal ? (articleData.content_before_rewrite || '') : (articleData.content || '')}
              onChange={(value) => !articleData._showOriginal && handleFieldChange('content', value)}
              placeholder="Contenu de l'article..."
            />
          )}
        </div>

        {/* Chiffre clé */}
        {articleData.key_figure && (
          <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
            <div className="flex items-center gap-4">
              <div>
                <p className="text-xs text-purple-400 uppercase mb-1">Chiffre clé</p>
                <p className="text-2xl font-bold text-purple-300">{articleData.key_figure}</p>
              </div>
              <div className="flex-1">
                <p className="text-sm text-slate-400">{articleData.key_figure_legend}</p>
              </div>
            </div>
          </div>
        )}

        {/* Coût de génération + Score SEO */}
        <div className="flex items-center justify-between text-xs">
          {articleData.cost_eur && (
            <span className="text-slate-500">Coût: {articleData.cost_eur.toFixed(2)} €</span>
          )}
          {articleData.seo_score !== undefined && (
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg font-bold ${
              articleData.seo_score >= 60 ? 'bg-green-500/10 text-green-400 border border-green-500/30' :
              articleData.seo_score >= 40 ? 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/30' :
              'bg-red-500/10 text-red-400 border border-red-500/30'
            }`} data-testid="seo-score">
              <span>SEO: {articleData.seo_score}/100</span>
              {articleData.seo_score < 60 && <span className="text-xs font-normal">(min. 60 requis)</span>}
            </div>
          )}
        </div>

        {/* Mots-clés */}
        <div>
          <p className="text-xs text-slate-500 uppercase mb-2">Mots-clés</p>
          <div className="flex flex-wrap gap-2">
            {articleData.keywords?.map((kw, i) => (
              <span key={i} className="px-2 py-1 bg-slate-800 text-slate-300 text-xs rounded">
                {kw}
              </span>
            ))}
          </div>
        </div>

        {/* Boutons Sauvegarder + Édition avancée */}
        <div className="flex gap-3 pt-2">
          <Button
            onClick={handleSave}
            className="bg-slate-800/50 hover:bg-slate-800 text-slate-300 border border-slate-700 font-semibold py-2 px-4 rounded-lg"
          >
            <Save className="w-4 h-4 mr-2" />
            Sauvegarder
          </Button>
          <Button
            onClick={() => navigate(`/article/${articleData.id || articleData.article_id}`)}
            className="bg-purple-500/10 hover:bg-purple-500/20 text-purple-400 border border-purple-500/50 font-semibold py-2 px-4 rounded-lg"
          >
            <Pencil className="w-4 h-4 mr-2" />
            Édition avancée
          </Button>
        </div>

        {/* Boutons de publication */}
        <PublishSection
          siteId={siteId}
          articleData={articleData}
          isExtra={isExtra}
          sites={sites}
          publishedArticles={publishedArticles}
          publishing={publishing}
          onPublish={onPublish}
        />
      </CardContent>
    </Card>
  );
};

// Sous-composant pour le choix du titre
const TitleChoice = ({ siteId, articleData, onUpdateArticle }) => {
  // Les propositions de titres générées par l'IA (factuel → clickbait)
  const titleProposals = articleData.title_proposals || [];
  
  // Styles pour chaque type de titre avec classes statiques (Tailwind JIT)
  const styleConfig = {
    'utilisateur': { 
      bgSelected: 'bg-cyan-500/10', 
      borderSelected: 'border-cyan-500/50',
      textColor: 'text-cyan-400',
      accent: 'accent-cyan-500',
      label: 'Votre titre', 
      icon: '👤' 
    },
    'factuel': { 
      bgSelected: 'bg-slate-500/10', 
      borderSelected: 'border-slate-500/50',
      textColor: 'text-slate-400',
      accent: 'accent-slate-500',
      label: 'Factuel', 
      icon: '📰' 
    },
    'informatif': { 
      bgSelected: 'bg-blue-500/10', 
      borderSelected: 'border-blue-500/50',
      textColor: 'text-blue-400',
      accent: 'accent-blue-500',
      label: 'Informatif', 
      icon: '📋' 
    },
    'engageant': { 
      bgSelected: 'bg-emerald-500/10', 
      borderSelected: 'border-emerald-500/50',
      textColor: 'text-emerald-400',
      accent: 'accent-emerald-500',
      label: 'Engageant', 
      icon: '✨' 
    },
    'accrocheur': { 
      bgSelected: 'bg-amber-500/10', 
      borderSelected: 'border-amber-500/50',
      textColor: 'text-amber-400',
      accent: 'accent-amber-500',
      label: 'Accrocheur', 
      icon: '🎯' 
    },
    'clickbait': { 
      bgSelected: 'bg-red-500/10', 
      borderSelected: 'border-red-500/50',
      textColor: 'text-red-400',
      accent: 'accent-red-500',
      label: 'Clickbait', 
      icon: '🔥' 
    },
    'original': { 
      bgSelected: 'bg-purple-500/10', 
      borderSelected: 'border-purple-500/50',
      textColor: 'text-purple-400',
      accent: 'accent-purple-500',
      label: 'Original', 
      icon: '🤖' 
    },
  };
  
  // Si pas de propositions, afficher l'ancien système avec title_user/title_llm
  if (titleProposals.length === 0) {
    return (
      <div className="mb-4 p-3 bg-slate-900/60 rounded-lg border border-slate-700/50">
        <p className="text-xs text-slate-400 uppercase mb-2 font-semibold">Choisir le titre</p>
        <div className="space-y-2">
          {articleData.title_user && (
            <label className="flex items-start gap-3 p-2.5 rounded-lg cursor-pointer hover:bg-slate-800/50 transition-colors border border-transparent hover:border-cyan-500/30">
              <input
                type="radio"
                name={`title_choice_${siteId}`}
                checked={articleData.title === articleData.title_user}
                onChange={() => onUpdateArticle(siteId, { title: articleData.title_user, title_source: 'user' })}
                className="mt-0.5 accent-cyan-500"
                data-testid={`radio-title-user-${siteId}`}
              />
              <div className="flex-1 min-w-0">
                <span className="text-[10px] text-cyan-400 uppercase tracking-wider font-bold">Votre titre</span>
                <p className="text-sm text-slate-200 mt-0.5 break-words">{articleData.title_user}</p>
              </div>
            </label>
          )}
          {articleData.title_llm && (
            <label className="flex items-start gap-3 p-2.5 rounded-lg cursor-pointer hover:bg-slate-800/50 transition-colors border border-transparent hover:border-purple-500/30">
              <input
                type="radio"
                name={`title_choice_${siteId}`}
                checked={articleData.title === articleData.title_llm}
                onChange={() => onUpdateArticle(siteId, { title: articleData.title_llm, title_source: 'llm' })}
                className="mt-0.5 accent-purple-500"
                data-testid={`radio-title-llm-${siteId}`}
              />
              <div className="flex-1 min-w-0">
                <span className="text-[10px] text-purple-400 uppercase tracking-wider font-bold">Titre suggéré par l&apos;IA</span>
                <p className="text-sm text-slate-200 mt-0.5 break-words">{articleData.title_llm}</p>
              </div>
            </label>
          )}
        </div>
      </div>
    );
  }
  
  // Afficher les propositions de titres (du factuel au clickbait)
  return (
    <div className="mb-4 p-3 bg-slate-900/60 rounded-lg border border-slate-700/50">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-slate-400 uppercase font-semibold">Propositions de titres</p>
        <span className="text-[10px] text-slate-500">Du plus factuel au plus accrocheur</span>
      </div>
      <div className="space-y-2">
        {titleProposals.map((proposal, index) => {
          const config = styleConfig[proposal.style] || styleConfig['original'];
          const isSelected = articleData.title === proposal.title;
          
          return (
            <label 
              key={index}
              className={`flex items-start gap-3 p-2.5 rounded-lg cursor-pointer transition-all border ${
                isSelected 
                  ? `${config.bgSelected} ${config.borderSelected}` 
                  : 'hover:bg-slate-800/50 border-transparent hover:border-slate-600'
              }`}
              data-testid={`title-proposal-${siteId}-${index}`}
            >
              <input
                type="radio"
                name={`title_choice_${siteId}`}
                checked={isSelected}
                onChange={() => onUpdateArticle(siteId, { 
                  title: proposal.title, 
                  title_source: proposal.source,
                  title_style: proposal.style 
                })}
                className={`mt-0.5 ${config.accent}`}
                data-testid={`radio-title-proposal-${siteId}-${index}`}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm">{config.icon}</span>
                  <span className={`text-[10px] ${config.textColor} uppercase tracking-wider font-bold`}>
                    {config.label}
                  </span>
                  {proposal.source === 'user' && (
                    <span className="text-[9px] bg-cyan-500/20 text-cyan-400 px-1.5 py-0.5 rounded">VOTRE TITRE</span>
                  )}
                </div>
                <p className="text-sm text-slate-200 mt-1 break-words">{proposal.title}</p>
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
};

// Sous-composant pour les boutons de publication
const PublishSection = ({
  siteId,
  articleData,
  isExtra,
  sites,
  publishedArticles,
  publishing,
  onPublish,
}) => {
  if (isExtra) {
    return (
      <div className="pt-4 border-t border-amber-500/20">
        <p className="text-xs text-amber-400/60 font-mono mb-2">Article hors sites paramétrés — copier/coller ou éditer manuellement</p>
      </div>
    );
  }

  if (publishedArticles[siteId]) {
    return (
      <div className="pt-4 border-t border-slate-800">
        <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg space-y-3">
          <div className="flex items-center gap-2">
            <Check className="w-5 h-5 text-green-400" />
            <span className="text-green-400 font-semibold text-lg">
              {publishedArticles[siteId].status === 'publish' ? 'Publié avec succès !' : 'Enregistré en brouillon'}
            </span>
          </div>
          {publishedArticles[siteId].url && (
            <a 
              href={publishedArticles[siteId].url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 p-3 bg-cyan-500/10 border border-cyan-500/30 rounded-lg text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/20 transition-all"
            >
              <ExternalLink className="w-5 h-5 flex-shrink-0" />
              <span className="font-semibold">Voir l&apos;article en ligne</span>
              <span className="text-xs text-slate-500 truncate ml-auto">{publishedArticles[siteId].url}</span>
            </a>
          )}
        </div>
      </div>
    );
  }

  const handleDraftClick = () => {
    console.log('[PublishSection] Draft button clicked', { siteId, articleId: articleData?.id || articleData?.article_id });
    if (!onPublish) {
      console.error('[PublishSection] onPublish is undefined!');
      return;
    }
    onPublish(siteId, articleData, 'draft');
  };

  const handlePublishClick = () => {
    console.log('[PublishSection] Publish button clicked', { siteId, articleId: articleData?.id || articleData?.article_id });
    if (!onPublish) {
      console.error('[PublishSection] onPublish is undefined!');
      return;
    }
    onPublish(siteId, articleData, 'publish');
  };

  return (
    <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-slate-800">
      <Button
        onClick={handleDraftClick}
        disabled={publishing}
        data-testid={`btn-draft-${siteId}`}
        className="flex-1 bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 border-2 border-amber-500/50 hover:border-amber-400 font-bold py-4 rounded-lg"
      >
        <FileText className="w-4 h-4 mr-2" />
        Enregistrer en brouillon
      </Button>
      <Button
        onClick={handlePublishClick}
        disabled={publishing}
        data-testid={`btn-publish-${siteId}`}
        className="flex-1 bg-green-500/10 hover:bg-green-500/20 text-green-400 border-2 border-green-500/50 hover:border-green-400 font-bold py-4 rounded-lg"
      >
        {publishing ? (
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
        ) : (
          <Check className="w-4 h-4 mr-2" />
        )}
        Publier maintenant
      </Button>
    </div>
  );
};

export default GeneratedArticleCard;
